import React, { useEffect, useState } from "react";
import { Instagram, Facebook, Youtube, Twitter, Save, ExternalLink, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../components/AdminLayout";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const SOCIALS = [
  {
    key: "instagram",
    label: "Instagram",
    icon: Instagram,
    placeholder: "https://instagram.com/cultlyf",
    accent: "#E1306C",
  },
  {
    key: "facebook",
    label: "Facebook",
    icon: Facebook,
    placeholder: "https://facebook.com/cultlyf",
    accent: "#1877F2",
  },
  {
    key: "youtube",
    label: "YouTube",
    icon: Youtube,
    placeholder: "https://youtube.com/@cultlyf",
    accent: "#FF0000",
  },
  {
    key: "twitter",
    label: "Twitter / X",
    icon: Twitter,
    placeholder: "https://x.com/cultlyf",
    accent: "#FFFFFF",
  },
];

const empty = { instagram: "", facebook: "", youtube: "", twitter: "" };

const sanitize = (v) => (v || "").trim();
const isLikelyUrl = (v) => !v || /^https?:\/\//i.test(v);

export default function AdminProfile() {
  const { user } = useAuth();
  const [social, setSocial] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/admin/settings")
      .then((r) => setSocial({ ...empty, ...(r.data?.social || {}) }))
      .catch(() => toast.error("Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  const update = (key, value) => setSocial((s) => ({ ...s, [key]: value }));

  const save = async (e) => {
    e?.preventDefault();
    const payload = { social: {} };
    for (const k of Object.keys(empty)) {
      const v = sanitize(social[k]);
      if (v && !isLikelyUrl(v)) {
        toast.error(`${k.toUpperCase()} link must start with http(s)://`);
        return;
      }
      payload.social[k] = v;
    }
    setSaving(true);
    try {
      const r = await api.put("/admin/settings", payload);
      setSocial({ ...empty, ...(r.data?.social || {}) });
      toast.success("PROFILE SAVED", { description: "Social links are now live on the website footer." });
    } catch (err) {
      toast.error("Save failed", { description: err?.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout title="My Profile">
      <div className="max-w-3xl space-y-6" data-testid="admin-profile-page">
        {/* Admin info card */}
        <div className="border border-[#27272A] bg-[#0A0A0A] p-5 flex items-center gap-4">
          <div className="w-14 h-14 bg-[#CFF048] text-black font-display font-black text-xl flex items-center justify-center">
            {(user?.name || user?.email || "A")[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-black text-lg truncate">{user?.name || "Cult Lyf Admin"}</p>
            <p className="label-mono text-[#71717A] truncate">// {user?.email}</p>
          </div>
          <div className="label-mono text-[#CFF048] hidden sm:flex items-center gap-2">
            <UserIcon size={14} /> ROLE: ADMIN
          </div>
        </div>

        {/* Social links */}
        <form onSubmit={save} className="border border-[#27272A] bg-[#0A0A0A] p-6" data-testid="social-links-form">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
            <div>
              <p className="label-mono text-[#CFF048]">// SOCIAL LINKS</p>
              <h2 className="font-display font-black text-2xl uppercase tracking-tight mt-1">
                Connect the Cult
              </h2>
              <p className="text-[#A1A1AA] text-sm mt-2 max-w-lg">
                Paste full URLs. Empty fields are hidden from the website footer. Changes go live
                instantly on the storefront.
              </p>
            </div>
          </div>

          <div className="mt-6 space-y-4">
            {SOCIALS.map(({ key, label, icon: Icon, placeholder, accent }) => {
              const val = social[key] || "";
              return (
                <div key={key} className="grid sm:grid-cols-[180px_1fr_auto] gap-3 items-center">
                  <label
                    className="flex items-center gap-2 label-mono text-white"
                    style={{ borderLeft: `3px solid ${accent}`, paddingLeft: 10 }}
                  >
                    <Icon size={16} style={{ color: accent }} />
                    {label.toUpperCase()}
                  </label>
                  <input
                    type="url"
                    value={val}
                    onChange={(e) => update(key, e.target.value)}
                    placeholder={placeholder}
                    className="cult-input"
                    data-testid={`social-input-${key}`}
                  />
                  {val ? (
                    <a
                      href={val}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="border border-[#27272A] px-3 py-2 label-mono hover:border-[#CFF048] hover:text-[#CFF048] flex items-center gap-2"
                      data-testid={`social-open-${key}`}
                    >
                      <ExternalLink size={14} /> OPEN
                    </a>
                  ) : (
                    <span className="hidden sm:block" />
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex flex-wrap gap-3 items-center">
            <button
              type="submit"
              disabled={saving || loading}
              className="btn-neon flex items-center gap-2 disabled:opacity-50"
              data-testid="save-social-btn"
            >
              <Save size={16} /> {saving ? "SAVING..." : "SAVE & PUBLISH"}
            </button>
            {loading && <span className="label-mono text-[#71717A]">// LOADING CURRENT LINKS...</span>}
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}
